import { useState, useRef, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Settings24Regular, SignOut24Regular, ChartMultiple24Regular, NavigationRegular, QuestionCircle24Regular, Play24Regular, PersonArrowRight24Regular } from "@fluentui/react-icons";
import logo from "../../../assets/dn-white-logo.svg";
import { useShell, PKB_AGENT_ID } from "../../context/ShellContext";
import { NotificationBell } from "../NotificationBell/NotificationBell";
import "./GlobalTopbar.css";

export function GlobalTopbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { userInfo, activeAgent, userRole, isAdmin, userName, sidebarOpen, setSidebarOpen } = useShell();

  // Show agent name only when not on home AURA chat (PKB agent or no agent)
  const showAgentName = activeAgent && activeAgent.agentId !== PKB_AGENT_ID && location.pathname.startsWith("/agent/");

  const principal = userInfo?.clientPrincipal;
  const claims = principal?.claims;
  const displayName = useMemo(() => {
    // Prefer the resolved name from the backend (DB)
    if (userName && !userName.includes("@")) return userName;
    // Try standard name claims first
    const nameClaim = claims?.find(c =>
      c.typ === "name" ||
      c.typ === "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name"
    )?.val;
    if (nameClaim && !nameClaim.includes("@")) return nameClaim;
    // Try given + surname claims
    const given = claims?.find(c =>
      c.typ === "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname"
    )?.val;
    const surname = claims?.find(c =>
      c.typ === "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/surname"
    )?.val;
    if (given && surname) return `${given} ${surname}`;
    if (given) return given;
    // Parse "Firstname.Lastname@domain" from email/userDetails
    const details = principal?.userDetails || "";
    const local = details.split("@")[0];
    if (local.includes(".")) {
      return local.split(".").map(p => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase()).join(" ");
    }
    return details || "User";
  }, [userName, claims, principal?.userDetails]);
  const roleLabel = userRole === "ProductManager" ? "Product Manager" : userRole;
  const initials = useMemo(() => {
    const parts = displayName.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return displayName.slice(0, 2).toUpperCase();
  }, [displayName]);

  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [menuOpen]);

  return (
    <header className="global-topbar" data-tour="topbar">
      <div className="global-topbar-left">
        {/* Hamburger — only visible on mobile via CSS */}
        <button
          className="topbar-hamburger"
          aria-label={sidebarOpen ? "Close menu" : "Open menu"}
          aria-expanded={sidebarOpen}
          onClick={() => setSidebarOpen(!sidebarOpen)}
        >
          <NavigationRegular />
        </button>
        <a href="/" className="app-logo">
          <img src={logo} alt="Diebold Nixdorf" className="logo-image" />
          <span className="logo-separator" />
          <div className="logo-text">
            <div className="logo-title">AURA</div>
            <div className="logo-subtitle">Adaptive User Retail Assistant</div>
          </div>
        </a>
      </div>

      {showAgentName && (
        <div className="global-topbar-center">
          <div className="active-agent-badge">
            <span className="active-agent-name">{activeAgent.name}</span>
          </div>
        </div>
      )}

      <div className="global-topbar-right" ref={menuRef}>
        <NotificationBell />
        <div className="user-info">
          <span className="user-name">{displayName}</span>
          <span className="user-role">{roleLabel}</span>
        </div>
        <button
          className="user-avatar-btn"
          data-tour="user-menu"
          title={displayName}
          onClick={() => setMenuOpen(!menuOpen)}
          aria-expanded={menuOpen}
          aria-haspopup="menu"
        >
          <span>{initials}</span>
        </button>
        {menuOpen && (
          <div className="user-menu" role="menu">
            {isAdmin && (
              <button
                className="user-menu-item"
                role="menuitem"
                onClick={() => { navigate("/settings"); setMenuOpen(false); }}
              >
                <Settings24Regular />
                <span>Settings</span>
              </button>
            )}
            {isAdmin && (
              <button
                className="user-menu-item"
                role="menuitem"
                onClick={() => { navigate("/usage-dashboard"); setMenuOpen(false); }}
              >
                <ChartMultiple24Regular />
                <span>Usage Dashboard</span>
              </button>
            )}
            {isAdmin && (
              <button
                className="user-menu-item"
                role="menuitem"
                onClick={() => { navigate("/access-requests"); setMenuOpen(false); }}
              >
                <PersonArrowRight24Regular />
                <span>Access Requests</span>
              </button>
            )}
            <button
              className="user-menu-item"
              role="menuitem"
              onClick={() => { window.dispatchEvent(new Event("aura:start-tour")); setMenuOpen(false); }}
            >
              <Play24Regular />
              <span>Take a Tour</span>
            </button>
            <button
              className="user-menu-item"
              role="menuitem"
              onClick={() => { navigate("/help"); setMenuOpen(false); }}
            >
              <QuestionCircle24Regular />
              <span>Help & Manual</span>
            </button>
            <button
              className="user-menu-item"
              role="menuitem"
              onClick={() => { window.location.href = "/logout"; }}
            >
              <SignOut24Regular />
              <span>Logout</span>
            </button>
          </div>
        )}
      </div>
    </header>
  );
}
